import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ShieldCheck,
  Calculator,
  FileText,
  ClipboardCheck,
  Briefcase,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  MapPin,
  Phone,
  MessageSquare,
  AlertTriangle,
  HelpCircle,
  Globe,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';
import RelatedServices from '../components/RelatedServices';

const FreeZoneServicesUAE = () => {
  usePageSeo({
    title: 'UAE Free Zone Audit, Tax & Accounting Services',
    description:
      'Professional audit, accounting, VAT, corporate tax and compliance services for UAE free zone companies — Hamriyah, SAIF Zone, DMCC, DIC, Ajman & more.',
    path: '/free-zone-services-uae'
  });

  const freeZones = [
    { name: 'Hamriyah Free Zone Authority', emirate: 'Sharjah', path: '/hamriyah-free-zone-services', linked: true },
    { name: 'Sharjah Airport International Free Zone', emirate: 'Sharjah', path: '/saif-zone-services', linked: true },
    { name: 'Dubai Multi Commodities Centre', emirate: 'Dubai', path: '/dmcc-audit-services', linked: true },
    { name: 'Dubai Airport Freezone (DAFZA)', emirate: 'Dubai', path: '/dafza-services', linked: true },
    { name: 'Dubai Internet City', emirate: 'Dubai', path: '/dubai-internet-city-services', linked: true },
    { name: 'IFZA – International Free Zone Authority', emirate: 'Dubai', path: '/ifza-services', linked: true },
    { name: 'RAKEZ (Ras Al Khaimah Economic Zone)', emirate: 'Ras Al Khaimah', path: '/rakez-services', linked: true },
    { name: 'Ajman Free Zone', emirate: 'Ajman', path: '/ajman-free-zone-services', linked: true },
    { name: 'Sharjah Media City (Shams)', emirate: 'Sharjah', path: '/shams-services', linked: true },
    { name: 'SPC Free Zone (Sharjah Publishing City)', emirate: 'Sharjah', path: '/spc-free-zone-services', linked: true }
  ];

  const services = [
    {
      icon: ClipboardCheck,
      title: 'External Audit Services',
      description:
        'Annual financial statement audits, audit reports for licence renewal, and IFRS-based financial reporting aligned with UAE free zone authority requirements.',
      points: ['Financial Statement Audit', 'Management Reports', 'Audit Support for Renewals', 'IFRS-Based Reporting', 'SME Financial Reporting']
    },
    {
      icon: Calculator,
      title: 'Accounting & Bookkeeping',
      description:
        'Accurate, cloud-based bookkeeping that keeps your books audit-ready and supports timely VAT and Corporate Tax filings.',
      points: ['Zoho Books', 'QuickBooks Online', 'Monthly Bookkeeping', 'Bank Reconciliation', 'VAT Reconciliation']
    },
    {
      icon: FileText,
      title: 'Corporate Tax Services',
      description:
        'UAE Corporate Tax registration, assessment, return filing and Small Business Relief evaluation for free zone entities.',
      points: ['Corporate Tax Registration', 'Tax Return Filing', 'Small Business Relief Guidance', 'Tax Assessment Support', 'Compliance Review']
    },
    {
      icon: ShieldCheck,
      title: 'VAT Services',
      description:
        'End-to-end VAT registration, periodic filings, reconciliations and FTA correspondence handling for free zone businesses.',
      points: ['VAT Registration', 'VAT Filing', 'VAT Review', 'FTA Assistance', 'VAT Health Check']
    },
    {
      icon: AlertTriangle,
      title: 'AML & goAML Compliance',
      description:
        'AML risk assessments, goAML registration support, internal controls and documentation for DNFBPs and regulated entities.',
      points: ['AML Risk Assessment', 'goAML Support', 'Internal AML Audit', 'CDD Procedures', 'Compliance Documentation']
    }
  ];

  const whyChoose = [
    {
      title: 'UAE Compliance Focus',
      description: 'Deep understanding of UAE free zone compliance, reporting expectations and renewal-cycle audit requirements.'
    },
    {
      title: 'Practical SME Approach',
      description: 'Straightforward, professional guidance designed for startups and growing free zone businesses — without enterprise complexity.'
    },
    {
      title: 'Responsive Support',
      description: 'Fast communication and structured compliance assistance from registered, partner-led teams.'
    },
    {
      title: 'Multi-Service Capability',
      description: 'Audit, tax, accounting and advisory delivered under one roof — fewer vendors, joined-up compliance.'
    }
  ];

  const challenges = [
    'Missing or incomplete bookkeeping records',
    'Delayed VAT filing and reconciliation gaps',
    'Audit preparation challenges before licence renewal',
    'Corporate tax registration and applicability uncertainty',
    'Lack of structured accounting systems',
    'Compliance documentation gaps (ESR, AML, goAML)'
  ];

  const industries = [
    'Trading Companies',
    'E-Commerce',
    'IT & Technology',
    'Consultancy Firms',
    'Logistics',
    'Manufacturing',
    'Education',
    'Service Businesses'
  ];

  const faqs = [
    {
      q: 'Is audit mandatory for UAE free zone companies?',
      a: 'Audit requirements vary depending on the free zone authority, licence type and regulatory obligations. Many UAE free zones — including Hamriyah Free Zone and SAIF Zone — require audited financial statements at the time of licence renewal. WPAA helps confirm your exact obligation and prepares the financials accordingly.'
    },
    {
      q: 'Do free zone companies need Corporate Tax registration?',
      a: 'Yes. Most UAE free zone entities are required to register for Corporate Tax with the Federal Tax Authority, even if they expect to benefit from the 0% Qualifying Free Zone Person regime. We assess your eligibility and handle the full registration and filing cycle.'
    },
    {
      q: 'Can you support Zoho Books and QuickBooks?',
      a: 'Yes. We provide setup, migration, monthly bookkeeping and reconciliation support on both Zoho Books and QuickBooks Online — the two platforms most commonly used by free zone SMEs in the UAE.'
    },
    {
      q: 'Do you support dormant or low-activity companies?',
      a: 'Yes. We support dormant and low-activity free zone entities with simplified compliance reviews, accounting catch-up and audit coordination so they remain in good standing with their free zone authority.'
    },
    {
      q: 'Can you assist with AML and goAML compliance?',
      a: 'Yes. WPAA assists DNFBPs and regulated entities with AML compliance frameworks, goAML registration, risk assessments and ongoing documentation support.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="freezone-master-page">
      <JsonLd
        id="ld-fz-service"
        data={serviceSchema({
          name: 'UAE Free Zone Audit, Tax & Compliance Services',
          description:
            'Audit, accounting, VAT, Corporate Tax and AML compliance services for UAE free zone companies including Hamriyah Free Zone, SAIF Zone, DMCC, DIC, Ajman and more.',
          path: '/free-zone-services-uae',
          serviceType: 'Free Zone Compliance Services'
        })}
      />
      <JsonLd
        id="ld-fz-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'UAE Free Zone Services', path: '/free-zone-services-uae' }
        ])}
      />
      <JsonLd id="ld-fz-faq" data={faqSchema(faqs)} />

      {/* HERO */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-400 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Globe className="text-teal-400" size={32} />
              <span className="text-teal-400 font-semibold text-sm uppercase tracking-wider">UAE Free Zones</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              UAE Free Zone Audit, Tax & Compliance Services
            </h1>
            <p className="text-2xl text-teal-200 mb-6 font-semibold">
              Professional audit, accounting, VAT, corporate tax and advisory for UAE free zone companies.
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl">
              At White Paper Accounts Auditing, we support startups, SMEs and established businesses across UAE free
              zones with practical compliance solutions and professional financial reporting — built around the
              realities of each authority.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=free-zone-services">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg" data-testid="fz-consultation-btn">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="fz-whatsapp-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  WhatsApp Us
                </Button>
              </a>
              <a href="tel:+971501219341" data-testid="fz-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-8 px-4 bg-white border-b border-gray-100">
        <div className="container mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm font-medium text-gray-600">
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-teal-600" /> UAE Compliance Focused</span>
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-teal-600" /> SME & Startup Friendly</span>
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-teal-600" /> Audit | Tax | Advisory</span>
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-teal-600" /> FTA Compliance Support</span>
            <span className="flex items-center gap-2"><CheckCircle size={16} className="text-teal-600" /> Free Zone Experience</span>
          </div>
        </div>
      </section>

      {/* FREE ZONES WE SUPPORT */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Coverage</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Free Zones We Support</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Compliance, audit and tax support across the most active free zone authorities in the UAE.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {freeZones.map((zone, index) => {
              const Inner = (
                <Card className={`border-2 h-full transition-all ${zone.linked ? 'border-teal-200 hover:border-teal-500 hover:shadow-lg group' : 'border-gray-100'}`}>
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="text-teal-600" size={24} />
                    </div>
                    <div>
                      <h3 className={`font-bold text-gray-900 ${zone.linked ? 'group-hover:text-teal-600' : ''}`}>
                        {zone.name}
                      </h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <MapPin size={12} /> {zone.emirate}
                      </p>
                      {zone.linked && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 mt-2">
                          View dedicated page <ArrowRight size={12} />
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
              return zone.linked ? (
                <Link key={index} to={zone.path} data-testid={`fz-zone-${index}`}>
                  {Inner}
                </Link>
              ) : (
                <div key={index} data-testid={`fz-zone-${index}`}>{Inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR SERVICES */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Our Free Zone Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Five core practice areas, delivered as a joined-up compliance programme for free zone entities.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((svc, index) => {
              const Icon = svc.icon;
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-teal-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{svc.title}</h3>
                    <p className="text-gray-600 mb-4">{svc.description}</p>
                    <ul className="space-y-2">
                      {svc.points.map((p, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE WPAA */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Why Businesses Choose WPAA UAE</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {whyChoose.map((w, idx) => (
              <Card key={idx} className="border-0 shadow-md hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{w.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{w.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE CHALLENGES */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Common Issues</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">
                Common Compliance Challenges in UAE Free Zones
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Many businesses operating in UAE free zones face the same recurring obstacles around accounting,
                audit, VAT and corporate tax. WPAA helps simplify compliance and maintain organised financial
                reporting systems.
              </p>
            </div>
            <div className="space-y-3">
              {challenges.map((c, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Sectors</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Industries We Support</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {industries.map((ind, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 text-center border border-gray-100 hover:border-teal-500 transition-all">
                <Briefcase className="text-teal-600 mx-auto mb-2" size={28} />
                <p className="font-semibold text-gray-800 text-sm">{ind}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-gray-50 border border-gray-200 rounded-xl px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <RelatedServices
        services={[
          { title: 'Hamriyah Free Zone Services', description: 'Dedicated audit, VAT, CT and AML support for Hamriyah Free Zone entities.', path: '/hamriyah-free-zone-services', accent: 'teal' },
          { title: 'SAIF Zone Services', description: 'Compliance, audit and accounting support for SAIF Zone companies.', path: '/saif-zone-services', accent: 'emerald' },
          { title: 'Corporate Tax Services', description: '9% UAE Corporate Tax registration and annual filing support.', path: '/corporate-tax-uae', accent: 'blue' }
        ]}
      />

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-700 to-emerald-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Need Professional Compliance Support?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Speak with our team for practical audit, accounting, VAT and corporate tax support tailored for UAE free zone businesses.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to="/contact?service=free-zone-services">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="fz-cta-consultation-btn">
                Schedule Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="fz-cta-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-teal-100">
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="fz-cta-phone-1">
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="fz-cta-phone-2">
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeZoneServicesUAE;
